<script setup>
import { segmentedVariants, segmentVariants } from '@/components/ui/variants';

/**
 * A row of mutually exclusive options (radio group semantics).
 * options: [{ value, label, icon? }]
 */
const model = defineModel({ type: [String, Number], required: true });

const props = defineProps({
  options: { type: Array, required: true },
  label: { type: String, required: true },
  size: { type: String, default: 'md' },
  tone: { type: String, default: 'light' },
  block: { type: Boolean, default: false },
});

function onKey(event, index) {
  const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
  if (!step) return;
  event.preventDefault();
  const next = props.options[(index + step + props.options.length) % props.options.length];
  model.value = next.value;
  event.currentTarget.parentElement?.children[props.options.indexOf(next)]?.focus();
}
</script>

<template>
  <div role="radiogroup" :aria-label="label" :class="segmentedVariants({ tone, block })">
    <button
      v-for="(option, index) in options"
      :key="option.value"
      type="button"
      role="radio"
      :aria-checked="model === option.value"
      :tabindex="model === option.value ? 0 : -1"
      :class="segmentVariants({ size, tone, selected: model === option.value })"
      @click="model = option.value"
      @keydown="onKey($event, index)"
    >
      <iconify-icon v-if="option.icon" :icon="option.icon" aria-hidden="true" />
      <span>{{ option.label }}</span>
    </button>
  </div>
</template>
